import { z } from "zod";
import { ExpertProfileAiSummariesSchema } from "./expert-ai-summaries";
import { PublicReviewsStatsSchema, RatingBreakdownSchema } from "./reviews-v2/stats";
import { PublicReviewSchema } from "./reviews-v2/reviews";
import {
    ExpertCustomerStorySchema,
    ExpertFaqSchema,
    ExpertPackagedServiceSchema,
    ExpertPartnerSchema,
} from "./expert-details";

/**
 * A partner-directory (Shopify) review imported by the platform sync.
 * Returned by GET /api/v2/expert/:slug/partner-reviews.
 */
export const PartnerReviewItemSchema = z.object({
    id: z.number(),
    author: z.string().nullable().optional(),
    review_date: z.string().nullable().optional(),
    overall_rating: z.number().nullable().optional(),
    criteria: z
        .array(
            z.object({
                key: z.string(),
                label: z.string(),
                rating: z.number(),
            }),
        )
        .optional(),
    review_text: z.string().nullable().optional(),
    service_reviewed: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
    company_url: z.string().nullable().optional(),
    project_value_label: z.string().nullable().optional(),
});

export type PartnerReviewItem = z.infer<typeof PartnerReviewItemSchema>;

export const ExpertProfilePaginationSchema = z.object({
    cursor: z.string().nullable(),
    has_next: z.boolean(),
});

export const ExpertProfileCaseStudiesSchema = z.object({
    items: z.array(ExpertCustomerStorySchema),
    pagination: ExpertProfilePaginationSchema,
});

export const ExpertProfilePackagedServicesSchema = z.object({
    items: z.array(ExpertPackagedServiceSchema),
    pagination: ExpertProfilePaginationSchema,
});

export const ExpertProfileReviewsSchema = z.object({
    items: z.array(PublicReviewSchema),
    stats: PublicReviewsStatsSchema,
    pagination: ExpertProfilePaginationSchema,
});

/**
 * Consolidated public expert-profile payload. The three paginated content
 * sections embed their first page so consumers can hydrate infinite queries
 * without repeating an initial request.
 * Returned by GET /api/v2/expert-profiles/:slug.
 */
export const ExpertProfilePageSchema = z.object({
    id: z.number(),
    slug: z.string(),
    name: z.string(),
    title: z.string().nullable().optional(),
    avatar: z.string().nullable().optional(),
    type: z.string(),
    is_pro: z.boolean(),
    is_claimed: z.boolean(),
    flags: z.array(z.string()).optional(),
    can_receive_reviews: z.boolean(),
    partner_directory: z
        .object({
            partner_id: z.string(),
            reviews_rating: z.number().nullable(),
            reviews_count: z.number().nullable(),
            last_synced_at: z.string().nullable().optional(),
        })
        .nullable(),
    links: z.object({
        website: z.string().nullable(),
        shopify_directory: z.string().nullable(),
        linkedin: z.string().nullable(),
        github: z.string().nullable(),
        x: z.string().nullable(),
        fiverr: z.string().nullable(),
        upwork: z.string().nullable(),
        toptal: z.string().nullable(),
        storetasker: z.string().nullable(),
        other_listing: z.string().nullable(),
        booking_call: z.string().nullable(),
        google_merchant: z.string().nullable().optional(),
        contact_email: z.string().nullable(),
    }),
    partner_badges: z.array(ExpertPartnerSchema.extend({
        logo_media: z.unknown().nullable().optional(),
    })),
    faqs: z.array(ExpertFaqSchema),
    confidence_signals: z.lazy(() => ExpertConfidenceSignalsSchema),
    ai_summaries: ExpertProfileAiSummariesSchema.nullable().optional(),
    case_studies: ExpertProfileCaseStudiesSchema,
    packaged_services: ExpertProfilePackagedServicesSchema,
    reviews: ExpertProfileReviewsSchema,
});

export const ExpertPublicProfileSchema = ExpertProfilePageSchema;
export type ExpertProfilePage = z.infer<typeof ExpertProfilePageSchema>;
export type ExpertPublicProfile = ExpertProfilePage;

/** Returned by GET /api/v2/expert/:slug/partner-reviews. */
export const ExpertPartnerReviewsSchema = z.object({
    directory_url: z.string().nullable(),
    rating: z.number().nullable(),
    count: z.number(),
    rating_breakdown: RatingBreakdownSchema.optional(),
    items: z.array(PartnerReviewItemSchema),
});

export type ExpertPartnerReviews = z.infer<typeof ExpertPartnerReviewsSchema>;

/**
 * Public confidence-signals payload.
 * Returned by GET /api/v2/experts/:slug/confidence-signals.
 */
export const ExpertConfidenceSignalsSchema = z.object({
    total_points: z.number(),
    total_available_points: z.number(),
    /** Portion of `total_points` contributed by bonus signals. */
    bonus_points: z.number().optional(),
    groups: z.array(
        z.object({
            key: z.string(),
            label: z.string(),
            total_points: z.number(),
            total_available_points: z.number(),
            bonus_points: z.number().optional(),
            signals: z.array(
                z.object({
                    /**
                     * Null for a bonus signal defined directly on the expert:
                     * it has no stable public identifier, unlike a definition
                     * or a preset instance.
                     */
                    key: z.string().nullable(),
                    public_text: z.string(),
                    points: z.number(),
                    has_signal: z.boolean(),
                    n_factor: z.number().nullable().optional(),
                    /**
                     * What the signal actually contributed. For a bonus this
                     * can differ from `points` only by being withheld entirely
                     * — the group ceiling applies in full or not at all.
                     */
                    applied_points: z.number().optional(),
                    is_bonus: z.boolean().optional(),
                }),
            ),
        }),
    ),
});

export type ExpertConfidenceSignals = z.infer<typeof ExpertConfidenceSignalsSchema>;
